import type { JsonObject } from "./types";
import {
  normalizeComponentType,
  type FileViewMode,
  type LayoutComponent,
  type LayoutComponentType,
  type LayoutResponsiveProps,
  type VisibilityCondition,
} from "./templates";

export type LayoutSource = LayoutDocument | LayoutComponent[];
export type LayoutVersionSource = "system" | "preset" | "manual" | "orbit" | "import";

export interface LayoutElement {
  type: LayoutComponentType;
  props?: JsonObject;
  title?: string;
  binding?: string;
  columns?: number;
  children: string[];
  repeatFrom?: string;
  visibleWhen?: VisibilityCondition[];
  styleToken?: string;
  responsive?: LayoutResponsiveProps;
  className?: string;
}

export interface LayoutSection {
  name?: string;
  root: string;
  elements: Record<string, LayoutElement>;
}

export interface LayoutDocument {
  schemaVersion: 1;
  root: string;
  elements: Record<string, LayoutElement>;
  sections?: Record<string, LayoutSection>;
}

export interface LayoutVersion {
  id: string;
  fileTypeId: string;
  version: number;
  viewMode: FileViewMode;
  definition: LayoutDocument;
  source: LayoutVersionSource;
  createdAt: string;
  createdBy: string;
  summary?: string;
}

export type LayoutPatchOperation =
  | { op: "addElement"; id: string; element: LayoutElement }
  | { op: "removeElement"; id: string }
  | { op: "updateElement"; id: string; patch: Partial<Omit<LayoutElement, "children">> & { children?: string[] } }
  | { op: "appendChild"; parentId: string; childId: string }
  | { op: "insertChild"; parentId: string; childId: string; index: number }
  | { op: "moveElement"; id: string; parentId: string; index?: number }
  | { op: "replaceChildren"; parentId: string; children: string[] }
  | { op: "setRoot"; id: string };

export interface LayoutPatch {
  baseRevision: number;
  operations: LayoutPatchOperation[];
  summary?: string;
}

const clone = <T>(value: T): T => structuredClone(value);

export function emptyLayoutDocument(): LayoutDocument {
  return { schemaVersion: 1, root: "", elements: {} };
}

function uniqueId(preferred: string, elements: Record<string, LayoutElement>): string {
  const base = preferred.trim() || "element";
  if (!elements[base]) return base;
  let suffix = 2;
  while (elements[`${base}-${suffix}`]) suffix += 1;
  return `${base}-${suffix}`;
}

/** Converts the original nested component tree into the stable-id document used by editing tools. */
export function layoutDocumentFromComponents(components: readonly LayoutComponent[]): LayoutDocument {
  if (!components.length) return emptyLayoutDocument();
  const elements: Record<string, LayoutElement> = {};

  const visit = (component: LayoutComponent): string => {
    const id = uniqueId(component.id, elements);
    const children = (component.children ?? []).map(visit);
    elements[id] = {
      type: component.type,
      children,
      ...(component.props ? { props: clone(component.props) } : {}),
      ...(component.title ? { title: component.title } : {}),
      ...(component.binding ? { binding: component.binding } : {}),
      ...(component.columns != null ? { columns: component.columns } : {}),
      ...(component.repeatFrom ? { repeatFrom: component.repeatFrom } : {}),
      ...(component.visibleWhen?.length ? { visibleWhen: clone(component.visibleWhen) } : {}),
      ...(component.styleToken ? { styleToken: component.styleToken } : {}),
      ...(component.responsive ? { responsive: clone(component.responsive) } : {}),
    };
    return id;
  };

  const roots = components.map(visit);
  if (roots.length === 1) return { schemaVersion: 1, root: roots[0]!, elements };
  const root = uniqueId("layout-root", elements);
  elements[root] = { type: "stack", children: roots };
  return { schemaVersion: 1, root, elements };
}

export function normalizeLayoutDocument(source: LayoutSource | null | undefined): LayoutDocument {
  if (!source) return emptyLayoutDocument();
  if (Array.isArray(source)) return layoutDocumentFromComponents(source);
  const elements = Object.fromEntries(Object.entries(source.elements ?? {}).map(([id, element]) => [id, {
    ...clone(element),
    type: normalizeComponentType(element.type as string),
    children: Array.isArray(element.children) ? [...element.children] : [],
  }])) as Record<string, LayoutElement>;
  const sections = "sections" in source && source.sections && typeof source.sections === "object" ? Object.fromEntries(Object.entries(source.sections).map(([secId, sec]) => [secId, {
    ...(sec.name ? { name: sec.name } : {}),
    root: typeof sec.root === "string" ? sec.root : "",
    elements: Object.fromEntries(Object.entries(sec.elements ?? {}).map(([elId, el]) => [elId, {
      ...clone(el),
      type: normalizeComponentType(el.type as string),
      children: Array.isArray(el.children) ? [...el.children] : [],
    }])) as Record<string, LayoutElement>,
  }])) : undefined;
  return { schemaVersion: 1, root: typeof source.root === "string" ? source.root : "", elements, ...(sections ? { sections } : {}) };
}

export function layoutComponentsFromDocument(source: LayoutSource | null | undefined): LayoutComponent[] {
  const document = normalizeLayoutDocument(source);
  if (!document.root || !document.elements[document.root]) return [];
  const visiting = new Set<string>();
  const visit = (id: string): LayoutComponent | null => {
    const element = document.elements[id];
    if (!element || visiting.has(id)) return null;
    visiting.add(id);
    const children = element.children.map(visit).filter((child): child is LayoutComponent => child != null);
    visiting.delete(id);
    return {
      id,
      type: element.type,
      ...(element.props ? { props: clone(element.props) } : {}),
      ...(element.title ? { title: element.title } : {}),
      ...(element.binding ? { binding: element.binding } : {}),
      ...(element.columns != null ? { columns: element.columns } : {}),
      ...(children.length ? { children } : {}),
      ...(element.repeatFrom ? { repeatFrom: element.repeatFrom } : {}),
      ...(element.visibleWhen?.length ? { visibleWhen: clone(element.visibleWhen) } : {}),
      ...(element.styleToken ? { styleToken: element.styleToken } : {}),
      ...(element.responsive ? { responsive: clone(element.responsive) } : {}),
    };
  };
  const root = visit(document.root);
  if (!root) return [];
  return root.id === "layout-root" && root.type === "stack" ? root.children ?? [] : [root];
}

export function validateLayoutDocument(source: LayoutSource): string[] {
  const document = normalizeLayoutDocument(source);
  const errors: string[] = [];
  const ids = Object.keys(document.elements);
  if (!document.root) {
    if (ids.length) errors.push("layout root is required when elements exist");
    return errors;
  }
  if (!document.elements[document.root]) errors.push(`layout root does not exist: ${document.root}`);

  const parents = new Map<string, string[]>();
  for (const [id, element] of Object.entries(document.elements)) {
    if (!id.trim()) errors.push("layout element id is required");
    if (new Set(element.children).size !== element.children.length) errors.push(`layout element has duplicate children: ${id}`);
    for (const childId of element.children) {
      if (!document.elements[childId]) errors.push(`layout element ${id} references missing child: ${childId}`);
      const entries = parents.get(childId) ?? [];
      entries.push(id);
      parents.set(childId, entries);
    }
  }
  for (const [id, entries] of parents) if (entries.length > 1) errors.push(`layout element has multiple parents: ${id}`);

  const visited = new Set<string>();
  const active = new Set<string>();
  const walk = (id: string) => {
    if (active.has(id)) {
      errors.push(`layout contains a cycle at: ${id}`);
      return;
    }
    if (visited.has(id)) return;
    const element = document.elements[id];
    if (!element) return;
    active.add(id);
    element.children.forEach(walk);
    active.delete(id);
    visited.add(id);
  };
  if (document.root) walk(document.root);
  for (const id of ids) if (!visited.has(id)) errors.push(`layout element is unreachable from root: ${id}`);
  return [...new Set(errors)];
}

function removeFromParents(document: LayoutDocument, id: string): void {
  for (const element of Object.values(document.elements)) element.children = element.children.filter((childId) => childId !== id);
}

function removeSubtree(document: LayoutDocument, id: string): void {
  const element = document.elements[id];
  if (!element) return;
  element.children.forEach((childId) => removeSubtree(document, childId));
  delete document.elements[id];
  removeFromParents(document, id);
  if (document.root === id) document.root = "";
}

export function applyLayoutPatch(source: LayoutSource, patch: LayoutPatch): LayoutDocument {
  const document = normalizeLayoutDocument(source);
  for (const operation of patch.operations) {
    if (operation.op === "addElement") {
      if (document.elements[operation.id]) throw new Error(`Layout element already exists: ${operation.id}`);
      document.elements[operation.id] = { ...clone(operation.element), children: [...operation.element.children] };
    } else if (operation.op === "removeElement") {
      removeSubtree(document, operation.id);
    } else if (operation.op === "updateElement") {
      const current = document.elements[operation.id];
      if (!current) throw new Error(`Unknown layout element: ${operation.id}`);
      document.elements[operation.id] = { ...current, ...clone(operation.patch), children: operation.patch.children ? [...operation.patch.children] : current.children };
    } else if (operation.op === "appendChild" || operation.op === "insertChild") {
      const parent = document.elements[operation.parentId];
      if (!parent || !document.elements[operation.childId]) throw new Error("Layout parent and child must exist");
      removeFromParents(document, operation.childId);
      const index = operation.op === "appendChild" ? parent.children.length : Math.max(0, Math.min(parent.children.length, Math.trunc(operation.index)));
      parent.children.splice(index, 0, operation.childId);
    } else if (operation.op === "moveElement") {
      const parent = document.elements[operation.parentId];
      if (!parent || !document.elements[operation.id]) throw new Error("Layout element and destination parent must exist");
      removeFromParents(document, operation.id);
      const index = operation.index == null ? parent.children.length : Math.max(0, Math.min(parent.children.length, Math.trunc(operation.index)));
      parent.children.splice(index, 0, operation.id);
    } else if (operation.op === "replaceChildren") {
      const parent = document.elements[operation.parentId];
      if (!parent) throw new Error(`Unknown layout parent: ${operation.parentId}`);
      parent.children = [...operation.children];
    } else if (operation.op === "setRoot") {
      if (!document.elements[operation.id]) throw new Error(`Unknown layout root: ${operation.id}`);
      document.root = operation.id;
    }
  }
  const errors = validateLayoutDocument(document);
  if (errors.length) throw new Error(`Invalid layout patch: ${errors.join("; ")}`);
  return document;
}
